import { useQuery } from "react-query"
import { ApplicationDispatch } from '../store/application/types';
import { fetchData } from './fetch';
import { useDispatch } from 'react-redux';
import { DealerMiscFee } from "./IDealertrackDealerDecisionData";
import type { FeatureFlagId } from "../utils/featureFlagIds";

export interface IConfiguration {
  clientId: string;
  clientName: string;
  clientLogoUrl: string;
  clientTimeZone: string;
  isSuperUser?: boolean;
  featureFlags: Partial<Record<FeatureFlagId, boolean>>;
  googleMapsEmbedKey?: string;
  isFinanceCompany?: boolean;
  maxPaymentAsPercentageOfNetIncome?: number;
  maxNumberOfApplicants?: number;
  customerStateIdOptions: string[];
  reDeskStatuses?: string[];
  mergeableStatuses?: string[];
  /** Application status ids that lock the application for editing (tenant `LockedApplicationStatuses` config item). */
  lockedApplicationStatuses: string[];
  /** Application status ids in which the indirect quoter is read-only for internal-documents indirect deals (tenant `InternalDocumentsIndirectQuoterReadOnlyStatuses` config item). */
  internalDocumentsIndirectQuoterReadOnlyStatuses: string[];
  defaultJdPowersportValueType?: string;
  applicationChatRecipientRoles: string[];
  financeQuoterDisabledFields: string[];
  leaseQuoterDisabledFields: string[];
  jdPowerSelectionDefault?: string;
  quotePresentationReport?: string;
  requiredChecklistsForDesking: string[];
  texasOcccRatePreference?: number;
  applicationListReport?: string;
  defaultAMSLoanClassId?: string;
  downPaymentReceiptReport?: string;
  applicationQueueColumnSortOrder?: string[];
  applicationRecapReport?: string;
  applicationAmortizationScheduleReport?: string;
  washoutReport?: string;
  applicationReport?: string;
  crmUrl?: string;
  financeQuoterCutoverDate?: Date;
  hiddenOverviewScreenWidgets?: string[];
  numberOfApplicationFinanceQuotes: number;
  miscFeeInformationList?: DealerMiscFee[];
  deferredDownEditorCutoverDate: Date;
  availableHardPullCreditBureaus: string[];
  availableSoftPullCreditBureaus: string[];
  losBureauPageXmlCutoverDate: Date;
  amsLoanPostingDialogDefaultCodes: IAmsLoanPostingDialogDefaultCodes | undefined;
  downPaymentDialogDefaultCodes: IDownPaymentDialogDefaultCodes | undefined;
  usePassTime: boolean;
  useCpi: boolean;
  /** AMS 2000's REQUIRE_DP: a deal posts only once its down payment is paid in full. Missing means on. */
  requireDownPayment?: boolean;
  amsCustomerNumberPrefix?: string;
  applicationFundingAprComplianceRange: number;
  decisionReportApproved: string;
  decisionReportConditional: string;
  decisionReportDeclined: string;
  clientHasDbSyncUrl: boolean;
  // Whether the client has a JD Power API key (never the key itself).
  jdPowerApiKeyConfigured: boolean;
  defaultSalesperson1Commission: number;
  deferredDownMaxPayments: number;
}

export const useConfiguration = () => {
  const dispatch = useDispatch<ApplicationDispatch>();

  const getConfiguration = async (key: string) => {
    const url = `api/configuration`;
    const response = await fetchData(url, dispatch) as IConfiguration;
    return response;
  }

  return useQuery(
    ['Configuration'],
    getConfiguration,
    {
      refetchOnWindowFocus: false,
    }
  );
}

export interface IAmsLoanPostingDialogDefaultCodes {
  customerNumberCreationMethod?: AmsCustomerNumberCreationMethod;
  locationOfPaymentCode?: string;
  locationOfPurchaseId?: string;
  dealerNumberCode?: string;
  lateFeeCode?: string;
  loanCode?: string;
  loanTypeCode?: string;
  loanOwnerCode?: string;
  advertisingCode?: string;
  portfolioNumberCode?: string;
  receiptMessageCode?: string;
  collectorEmployeeNumber?: string;
  interestTypeCode?: string;
}

export interface IDownPaymentDialogDefaultCodes {
  paymentSourceCode?: string;
  deliveryMethodCode?: string;
  locationOfPaymentCode?: string;
}

export enum AmsCustomerNumberCreationMethod {
  CarStockNumber = 1,
  SerialNumber = 2, // 0000000001, 0000000002, 0000000003, etc
  Freehand = 3, // They can type in whatever their little hearts desire
  ApplicationNumber = 4
}
